/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import type { FilterSection as SidebarSection } from '../../../../shared/FilterSidebar';
import { SEVERITY_LABELS } from './SeverityBadge';
import { QUARANTINE_STRINGS as STRINGS } from './quarantine.strings';
import {
  SEVERITIES,
  type FacetValue,
  type FilterSection,
  type QuarantineFacets,
  type QuarantineFilters,
} from './quarantine.types';

const ECOSYSTEM_LABELS: Record<string, string> = {
  maven: 'Maven',
  npm: 'npm',
  nuget: 'NuGet',
  pypi: 'PyPI',
};

const SECTIONS: { id: FilterSection; label: string }[] = [
  { id: 'severity', label: STRINGS.filters.severity },
  { id: 'policy', label: STRINGS.filters.policy },
  { id: 'ecosystem', label: STRINGS.filters.ecosystem },
  { id: 'repository', label: STRINGS.filters.repository },
];

function optionsFor(id: FilterSection, facetValues: FacetValue[]) {
  switch (id) {
    case 'severity': {
      const rank = (value: string) => {
        const index = (SEVERITIES as string[]).indexOf(value);
        return index === -1 ? SEVERITIES.length : index;
      };
      return [...facetValues]
        .sort((a, b) => rank(a.value) - rank(b.value))
        .map((f) => ({
          value: f.value,
          label: SEVERITY_LABELS[f.value as keyof typeof SEVERITY_LABELS] ?? f.value,
          count: f.count,
        }));
    }
    case 'ecosystem':
      return facetValues.map((f) => ({
        value: f.value,
        label: ECOSYSTEM_LABELS[f.value.toLowerCase()] ?? f.value,
        count: f.count,
      }));
    default:
      return facetValues.map((f) => ({ value: f.value, label: f.value, count: f.count }));
  }
}

export function buildFilterSections(
  facets: QuarantineFacets | undefined,
  filters: QuarantineFilters
): SidebarSection[] {
  return SECTIONS.map(({ id, label }) => ({
    id,
    label,
    type: 'checkbox' as const,
    value: filters[id],
    options: optionsFor(id, facets?.[id] ?? []),
  }));
}
